//@ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import * as stripe from '../services/stripe/stripe';
import { CheckoutCurrencyController } from '../utils/payment/checkoutCurrencyController';

/** Shared checkout owner; UI receives only backend-verified charge amounts. */
const useCheckoutCurrency = (config) => {
  const controllerRef = useRef(null);
  const sessionRef = useRef(config.sessionKey);
  if (!controllerRef.current || sessionRef.current !== config.sessionKey) {
    controllerRef.current = new CheckoutCurrencyController(stripe);
    sessionRef.current = config.sessionKey;
  }
  const controller = controllerRef.current;
  const requestCurrency = useCallback(
    (currency) => controller.requestCurrency(currency),
    [controller]
  );
  const [ownedState, setOwnedState] = useState(() => ({
    owner: controller,
    value: controller.snapshot()
  }));
  const state =
    ownedState.owner === controller ? ownedState.value : controller.snapshot();
  const setState = useCallback(
    (value) => setOwnedState({ owner: controller, value }),
    [controller]
  );
  const latest = useRef(config);
  latest.current = config;
  const footprint = JSON.stringify([
    config.checkout,
    config.operatorAmount,
    config.operatorBookingCredit,
    config.paymentMethod,
    config.initialIntent,
    config.enabled,
    config.active,
    config.prefetch,
    config.processing,
    config.quoteContext
  ]);
  useEffect(() => {
    controller.config.onIntentChange = (...args) =>
      controllerRef.current === controller &&
      latest.current.onIntentChange?.(...args);
    const unsubscribe = controller.subscribe(setState);
    clearTimeout(controller.disposeTimer);
    controller.disposed = false;
    return () => {
      unsubscribe();
      // React StrictMode immediately reconnects the same owner. Actual unmount
      // disposes it after that reconnect opportunity without creating a PI.
      controller.disposeTimer = setTimeout(() => controller.dispose(), 0);
    };
  }, [controller, setState]);
  useEffect(() => {
    controller.configure({
      ...latest.current,
      onIntentChange: (...args) =>
        controllerRef.current === controller &&
        latest.current.onIntentChange?.(...args)
    });
    setState(controller.snapshot());
    if (
      latest.current.enabled &&
      (latest.current.active || latest.current.prefetch)
    )
      controller.discover();
  }, [controller, footprint, setState]);
  useEffect(() => {
    const expiresAt = state.selection?.expiresAt;
    if (!expiresAt) return;
    const update = () => setState(controller.snapshot());
    const timer = setTimeout(
      update,
      Math.max(1, Math.min(2147483647, expiresAt * 1000 - Date.now() + 1))
    );
    window.addEventListener('focus', update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', update);
    };
  }, [controller, state.selection?.expiresAt, setState]);
  return {
    ...state,
    requestCurrency,
    refresh: () => controller.refresh(),
    confirmReconfirmation: () => controller.confirmReconfirmation(),
    retry: () => controller.retry(),
    beginPayment: (selection) => controller.beginPayment(selection),
    endPayment: (result) => controller.endPayment(result),
    reset: () => controller.reset()
  };
};
export default useCheckoutCurrency;
